import React, { useState, useEffect } from "react";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { isAfter, parse, isValid, format, isEqual } from "date-fns";

import {
  Flex,
  SimpleGrid,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack,
} from "@chakra-ui/react";

import {
  Permission,
  criancaFunc,
  referenciaFunc,
} from "../../../../../../../helpers/Funcionalidades";
import CriancaService from "../../../../../../../main/services/CriancaService";
import { CustomButton } from "../../../../../../components/CustomButton";
import CustomDropzone from "../../../../../../components/CustomDropzone";
import { CustomSelectWField } from "../../../../../../components/CustomSelectWField";
import { FocusError } from "../../../../../../components/FocusError";
import { CustomDatepickerWField } from "../../../../../../components/CustomDatepickerWField";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RestricaoService from "../../../../../../../main/services/RestricaoService";
import { hasFormChanged } from "../../../../../../utils/hasFormChanged";
import { useAppContext } from "../../../../../../../Context/MessageContextProvider";
import { createCustomFile } from "../../../../../../utils/createCustomFile";
import { useHistory, useLocation } from "react-router-dom";
import { deleteUrlParam } from "../../../../../../utils/deleteUrlParam";

const suportedFormats = [
  ".png",
  ".bmp",
  ".tiff",
  ".jpg",
  ".jpeg",
  ".doc",
  ".docx",
  ".txt",
  ".dot",
  ".pdf",
  ".xml",
  ".xls",
  ".xlam",
  ".ods",
];

const schema = Yup.object().shape({
  pessoa: Yup.object().nullable().required("Este campo é obrigatório!"),
  dataProibicao: Yup.string().when("liberar", {
    is: false,
    then: Yup.string()
      .required("Este campo é obrigatório!")
      .nullable()
      .test("validDate", "Data inválida!", (value) => {
        if (!!value) {
          if (value.length === 10) {
            return isValid(parse(value, "dd/MM/yyyy", new Date()));
          } else {
            return false;
          }
        } else {
          return true;
        }
      })
      .test(
        "dataNascimentoAfterToday",
        "A data informada não pode ser uma data futura!",
        (value) => {
          if (!value) {
            return true;
          } else {
            return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
          }
        }
      ),
  }),

  dataLiberacao: Yup.string().when("liberar", {
    is: true,
    then: Yup.string()
      .required("Este campo é obrigatório!")
      .nullable()
      .test("validDate", "Data inválida!", (value) => {
        if (!!value) {
          if (value.length === 10) {
            return isValid(parse(value, "dd/MM/yyyy", new Date()));
          } else {
            return false;
          }
        } else {
          return true;
        }
      })
      .test(
        "dataNascimentoAfterToday",
        "A data informada não pode ser uma data futura!",
        (value) => {
          if (!value) {
            return true;
          } else {
            return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
          }
        }
      )
      .test(
        "dataLiberacaoAfterProibicao",
        "Data da liberação menor que a data da proibição.",
        function (value) {
          const { dataProibicao } = this.parent;

          if (dataProibicao && value) {
            return (
              isAfter(
                parse(value, "dd/MM/yyyy", new Date()),
                parse(dataProibicao, "dd/MM/yyyy", new Date())
              ) ||
              isEqual(
                parse(value, "dd/MM/yyyy", new Date()),
                parse(dataProibicao, "dd/MM/yyyy", new Date())
              )
            );
          } else {
            return true;
          }
        }
      ),
  }),

  anexoGuia: Yup.mixed().test(
    "fileType",
    "Anexar uma extensão válida.",
    (value) => {
      if (value && value.path) {
        let haveFormat = false;
        suportedFormats.forEach((format) => {
          if (value.path.toString().toLowerCase().includes(format)) {
            haveFormat = true;
          }
        });
        return haveFormat;
      } else {
        return true;
      }
    }
  ),
});

export const RestricoesEditar = ({
  isOpen,
  onOpen,
  onClose,
  filiacoes,
  setFieldValue,
  selectedFiliacao,
  updateFromVisualizacao,
  dataNascimentoCA,
  isVisualizacao,
  secondOnClose,
  roleKeycloack,
  idCrianca,
  fetchDataRestricoes,
  isLiberar,
  isLiberarEdit,
}) => {
  //const [isVisual] = useState(!!isVisualizacao);
  const history = useHistory();
  const location = useLocation();
  const gestor = roleKeycloack.includes(criancaFunc.visualizarTudo);
  const [relations, setRelations] = useState({
    options: [],
    isLoading: false,
  });
  const [initialValues, setInitialValues] = useState({
    pessoa: "",
    dataProibicao: "",
    dataLiberacao: "",
    anexoGuia: "",
    liberar: isLiberar,
    gestor: gestor,
  });
  const { editarRestricaoVisita, setEditarRestricaoVisita } = useAppContext();
  const [removeu, setRemoveu] = useState(false);
  const [removeuLiberacao, setRemoveuLiberacao] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");
  const idAnexo = searchParams.get("idAnexo");

  const backAlert = () => {
    if (editarRestricaoVisita) {
      Swal.fire({
        title: "Tem certeza que deseja cancelar?",
        text: "As informações serão perdidas.",
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: "#169499",
        cancelButtonColor: "#169499",
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
      }).then((result) => {
        if (result.isConfirmed) {
          onClose();
          setEditarRestricaoVisita(false);
        }
      });
    } else {
      onClose();
    }
  };

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  useEffect(() => {
    const {
      pessoaRestricao,
      vinculo,
      possuiAnexoRestricao,
      dataProibicao,
      dataLiberacao,
      possuiAnexoLiberacao,
      anexoRestricao,
    } = selectedFiliacao;

    const custom_date = new Date(dataProibicao).toLocaleDateString("pt-BR");
    const custom_dateLib = new Date(dataLiberacao).toLocaleDateString("pt-BR");

    const fakeFile = createCustomFile(
      [anexoRestricao?.arquivoBase64 ? anexoRestricao?.arquivoBase64 : ""],
      anexoRestricao?.arquivoNome ? anexoRestricao?.arquivoNome : "",
      {
        type: anexoRestricao?.arquivoTipo ? anexoRestricao?.arquivoTipo : "",
        idAnexo: anexoRestricao?.idAnexo ? anexoRestricao?.idAnexo : 0,
      }
    );

    setInitialValues({
      pessoa: {
        id: pessoaRestricao.id,
        nome: `${pessoaRestricao.nome + " - " + vinculo}`,
      },
      dataProibicao: dataProibicao ? custom_date : "",
      dataLiberacao: dataLiberacao ? custom_dateLib : "",
      liberar: isLiberar ? isLiberar : false,
      anexoGuia:
        (isLiberar && possuiAnexoLiberacao) ||
        (!isLiberar && possuiAnexoRestricao)
          ? possuiAnexoRestricao
            ? fakeFile
            : null
          : null,
    });
  }, [selectedFiliacao, isLiberar]);

  const handleGetRelacionamento = () => {
    setRelations({ ...relations, isLoading: true });

    CriancaService.getRelationamentosGuardaReferencia(idCrianca).then((res) => {
      const tempOptions = res.data.map((relation) => {
        return {
          id: relation.id,
          nome: relation.nome`${
            relation.nomeSocial ? ` / ${relation?.nomeSocial}` : ""
          }`,
        };
      });
      setRelations({ options: tempOptions, isLoading: false });
    });
  };

  const editarRestricao = async (idCrianca, id, valuesToSend) => {
    try {
      await RestricaoService.putRestricao(idCrianca, id, valuesToSend);
      (["8", "9"].includes(idAnexo) ||
        ["44", "45"].includes(idTipoPendencia)) &&
        history.replace(location.pathname);
      Swal.fire({
        title: "Sucesso!",
        text: "Registro Atualizado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {});
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Atenção!",
        text: error.response.data.message ? error.response.data.message : "",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    } finally {
      onClose();
      fetchDataRestricoes();
    }
  };

  const liberarRestricao = async (idCrianca, idRestricao, valuesToSend) => {
    try {
      await RestricaoService.liberarRestricao(
        idCrianca,
        idRestricao,
        valuesToSend
      );
      deleteUrlParam("idAnexo", history);
      (["8", "9"].includes(idAnexo) ||
        ["44", "45"].includes(idTipoPendencia)) &&
        history.replace(location.pathname);
      Swal.fire({
        title: "Sucesso!",
        text:
          isLiberar && isLiberarEdit
            ? "Registro Atualizado com sucesso"
            : isLiberar
            ? "Sucesso! Registro cadastrado com sucesso!"
            : "",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {});
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Atenção!",
        text: error.response.data.message ? error.response.data.message : "",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    } finally {
      onClose();
      fetchDataRestricoes();
    }
  };

  const handleSubmit = async (values, actions) => {
    const { pessoa, anexoGuia, dataProibicao, dataLiberacao } = values;
    const splitDate = isLiberar
      ? dataLiberacao?.split("/")
      : dataProibicao?.split("/");

    let formattedDateString = "";
    if (splitDate && splitDate.length === 3) {
      const year = parseInt(splitDate[2]);
      const month = parseInt(splitDate[1]) - 1;
      const day = parseInt(splitDate[0]);
      const now = new Date();

      const formattedDate = new Date(
        year,
        month,
        day,
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      formattedDateString = format(
        formattedDate,
        "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
      );
    }

    const valuesToSend = {
      idPessoaFisicaRestricao: pessoa ? pessoa.id : "",
      anexoGuia: anexoGuia ? anexoGuia : "",
      dataProibicao: dataProibicao ? formattedDateString : "",
      dataLiberacao: dataLiberacao ? formattedDateString : "",
    };

    if (!isLiberar) {
      const anexoRequest =
        selectedFiliacao?.possuiAnexoRestricao && removeu
          ? selectedFiliacao.idRestricao
          : 0;

      if (
        (removeu && anexoGuia) ||
        (anexoGuia && selectedFiliacao?.possuiAnexoRestricao === false)
      ) {
        await RestricaoService.postAnexoTermoRestricao(
          idCrianca,
          anexoGuia,
          anexoRequest
        ).then((res) => {
          const { idAnexo, idCrianca } = res.data;

          const valuesToSend = {
            idAnexoRestricao: idAnexo ? idAnexo : null,
            idPessoaFisicaRestricao: pessoa ? pessoa.id : "",
            dataProibicao: dataProibicao ? formattedDateString : "",
          };

          if (idAnexo && idCrianca) {
            editarRestricao(
              idCrianca,
              selectedFiliacao.idRestricao,
              valuesToSend
            );
          }
        });
      } else {
        valuesToSend.idAnexoRestricao = removeu ? 0 : null;
        delete valuesToSend.dataLiberacao;
        delete valuesToSend.anexoGuia;

        await editarRestricao(
          idCrianca,
          selectedFiliacao.idRestricao,
          valuesToSend
        );
      }
    } else {
      delete valuesToSend.dataProibicao;

      const anexoRequest =
        selectedFiliacao?.possuiAnexoLiberacao && removeuLiberacao
          ? selectedFiliacao?.idRestricao
          : 0;
      if (
        (removeuLiberacao && anexoGuia) ||
        (anexoGuia && selectedFiliacao?.possuiAnexoLiberacao === false)
      ) {
        await RestricaoService.postAnexoTermoLiberacao(
          idCrianca,
          anexoGuia,
          anexoRequest
        ).then((res) => {
          const { idAnexo, idCrianca } = res.data;

          const valuesToSend = {
            idAnexoLiberacao: idAnexo ? idAnexo : null,
            dataLiberacao: formattedDateString,
          };

          if (idAnexo && idCrianca) {
            liberarRestricao(
              idCrianca,
              selectedFiliacao.idRestricao,
              valuesToSend
            );
          }
        });
      } else {
        valuesToSend.idAnexoLiberacao = removeuLiberacao ? 0 : null;
        delete valuesToSend.dataProibicao;
        delete valuesToSend.anexoGuia;

        await liberarRestricao(
          idCrianca,
          selectedFiliacao.idRestricao,
          valuesToSend
        );
      }
    }

    actions.setSubmitting(false);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent
          p={{ base: "5px", sm: "15px", md: "25px" }}
          w={{ base: "100%", lg: "70%" }}
          bgColor="#FFF"
        >
          <ModalHeader
            p="0px"
            fontWeight={700}
            fontSize="14px"
            lineHeight="19.6px"
            color="#169499"
          >
            {isLiberarEdit && isLiberar
              ? "Editar Liberação".toUpperCase()
              : isLiberar
              ? "Liberação".toUpperCase()
              : "Editar Proibição".toUpperCase()}
          </ModalHeader>
          <ModalBody
            p="0px"
            mt="25px"
            minH={{ base: "0px", md: "200px" }}
            pl="1px"
          >
            <Formik
              validationSchema={schema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize
            >
              {(props) => (
                <Form style={{ width: "100%", paddingBottom: "5px" }}>
                  {setEditarRestricaoVisita(
                    hasFormChanged(initialValues, props.values)
                  )}
                  <FocusError />
                  <input
                    type="text"
                    style={{ position: "absolute", left: "-9999px" }}
                  />
                  <Grid templateColumns="repeat(3, 1fr)" gap="15px" w="100%">
                    <GridItem colSpan={{ base: 6 }}>
                      <CustomSelectWField
                        fieldName="pessoa"
                        label="Pessoa com restrição a visitação*"
                        placeholder="Selecione a pessoa com restrição"
                        getOptionLabel={(option) => option.nome}
                        getOptionValue={(option) => option.id}
                        defaultValue={props.values.nome}
                        isLoading={relations.isLoading}
                        options={relations.options}
                        onChange={(value) => {
                          props.setFieldValue("pessoa", value ? value : "");
                        }}
                        onFocus={handleGetRelacionamento}
                        isDisabled={true}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 4, lg: 2 }}>
                      <VStack alignItems="flex-start">
                        <CustomDatepickerWField
                          fieldName={`${
                            isLiberar ? "dataLiberacao" : "dataProibicao"
                          }`}
                          label={`${
                            isLiberar
                              ? "Data da Liberação *"
                              : "Data da Proibição *"
                          }`}
                          maxDate={new Date()}
                          isDisabled={!gestor && !isLiberar}
                          placeholder="DD/MM/AAAA"
                        />
                      </VStack>
                    </GridItem>
                    <GridItem colSpan={{ base: 6, lg: 4 }} />
                    <Permission
                      roleKeycloack={roleKeycloack}
                      func={referenciaFunc.guardaAnexar}
                    >
                      <GridItem colSpan={{ base: 6, lg: 1 }}>
                        <Flex>
                          <CustomDropzone
                            formProps={props}
                            title={`Anexar Decisão Judicial`}
                            fieldName="anexoGuia"
                            height="42px"
                            titleText="Anexar Decisão Judicial"
                            setAnexoNaoEditado={() => {
                              if (!isLiberar) {
                                setRemoveu(true);
                              } else {
                                setRemoveuLiberacao(true);
                              }
                            }}
                          />
                        </Flex>
                      </GridItem>
                    </Permission>
                    <GridItem colSpan={{ base: 6, lg: 5 }}>
                      <Flex justifyContent="center">
                        <SimpleGrid
                          mt="25px"
                          columns={2}
                          spacing="15px"
                          w={{ base: "100%", lg: "80%" }}
                        >
                          <CustomButton onClick={backAlert} label="Cancelar" />

                          <CustomButton
                            type="submit"
                            translate="no"
                            isLoading={props.isSubmitting}
                            label={
                              isLiberar && isLiberarEdit
                                ? "Salvar"
                                : isLiberar
                                ? "Liberar"
                                : "Salvar"
                            }
                          />
                        </SimpleGrid>
                      </Flex>
                    </GridItem>
                  </Grid>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
